import React from "react";
import { Box, Typography, Button, IconButton } from "@mui/material";
import CameraEnhanceOutlinedIcon from "@mui/icons-material/CameraEnhanceOutlined";
import EditNoteOutlinedIcon from "@mui/icons-material/EditNoteOutlined";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import DeleteOutlinedIcon from "@mui/icons-material/DeleteOutlined";

/**
 * MethodSelection - Step 1: Choose AI OCR or Manual entry
 * Also shows saved drafts (fetched from API with draft: true) if any exist.
 *
 * @param {Function} onSelectAI        - Handler when AI option is selected
 * @param {Function} onSelectManual    - Handler when Manual option is selected
 * @param {Array}    drafts            - Array of draft menus from API (draft: true)
 * @param {Function} onContinueDraft   - (draft) => void — open draft for editing
 * @param {Function} onDeleteDraft     - (menuId) => void — delete a draft
 */
const MethodSelection = ({
  isCatalog = false,
  onSelectAI,
  onSelectManual,
  drafts = [],
  onContinueDraft,
  onDeleteDraft,
}) => {
  const label = isCatalog ? "catalogue" : "menu";

  return (
    <Box>
      {/* Title */}
      <Typography
        sx={{
          fontSize: { xs: "1.25rem", sm: "1.5rem" },
          fontWeight: 700,
          color: "#111827",
          textTransform: "lowercase",
          mb: 0.5,
        }}
      >
        how would you like to start?
      </Typography>
      <Typography
        sx={{
          fontSize: "0.875rem",
          color: "#6b7280",
          textTransform: "lowercase",
          mb: 3,
        }}
      >
        choose a method to digitize your restaurant's {label} items and pricing.
      </Typography>

      {/* Cards container */}
      <Box
        sx={{
          display: "flex",
          gap: 2,
          flexDirection: { xs: "column", sm: "row" },
        }}
      >
        {/* AI OCR Card */}
        <Box
          onClick={onSelectAI}
          sx={{
            flex: 1,
            border: "2px dashed #7C3AED",
            borderRadius: 3,
            p: { xs: 2.5, sm: 3 },
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            textAlign: "center",
            cursor: "pointer",
            position: "relative",
            bgcolor: "#FAFAFE",
            transition: "all 0.2s ease",
            "&:hover": {
              bgcolor: "#F3EEFF",
              borderColor: "#6200EE",
              transform: "translateY(-2px)",
              boxShadow: "0 4px 16px rgba(124, 58, 237, 0.15)",
            },
          }}
        >
          {/* Recommended badge */}
          <Box
            sx={{
              position: "absolute",
              top: 12,
              left: 12,
              display: "flex",
              alignItems: "center",
              gap: 0.5,
            }}
          >
            <Box
              sx={{
                bgcolor: "#7C3AED",
                color: "#fff",
                fontSize: "0.65rem",
                fontWeight: 700,
                px: 1,
                py: 0.25,
                borderRadius: 1,
                textTransform: "uppercase",
                letterSpacing: 0.5,
              }}
            >
              recommended
            </Box>
          </Box>

          {/* Sparkle icon top-right */}
          <AutoAwesomeIcon
            sx={{
              position: "absolute",
              top: 12,
              right: 12,
              fontSize: 20,
              color: "#7C3AED",
            }}
          />

          {/* Camera icon */}
          <Box
            sx={{
              mt: 4,
              mb: 2,
              width: 56,
              height: 56,
              borderRadius: "50%",
              bgcolor: "#F3EEFF",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <CameraEnhanceOutlinedIcon sx={{ fontSize: 28, color: "#7C3AED" }} />
          </Box>

          <Typography
            sx={{
              fontSize: { xs: "0.95rem", sm: "1.05rem" },
              fontWeight: 700,
              color: "#111827",
              textTransform: "lowercase",
              mb: 0.5,
            }}
          >
            scan or upload menu
          </Typography>
          <Typography
            sx={{
              fontSize: "0.8rem",
              color: "#6b7280",
              textTransform: "lowercase",
              mb: 2,
              lineHeight: 1.5,
            }}
          >
            our ai instantly extracts dishes, descriptions, and prices from your
            photos or pdf files to build your {label}.
          </Typography>

          <Button
            variant="contained"
            fullWidth
            onClick={(e) => {
              e.stopPropagation();
              onSelectAI();
            }}
            sx={{
              bgcolor: "#7C3AED",
              textTransform: "lowercase",
              fontWeight: 600,
              borderRadius: 2,
              py: 1,
              "&:hover": { bgcolor: "#6200EE" },
            }}
          >
            get started with ai
          </Button>
        </Box>

        {/* Manual Entry Card */}
        <Box
          onClick={onSelectManual}
          sx={{
            flex: 1,
            border: "2px dashed #d1d5db",
            borderRadius: 3,
            p: { xs: 2.5, sm: 3 },
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            textAlign: "center",
            cursor: "pointer",
            bgcolor: "#fff",
            transition: "all 0.2s ease",
            "&:hover": {
              borderColor: "#7C3AED",
              bgcolor: "#FAFAFE",
              transform: "translateY(-2px)",
              boxShadow: "0 4px 16px rgba(0, 0, 0, 0.08)",
            },
          }}
        >
          {/* Edit icon */}
          <Box sx={{ mb: 2 }}>
            <EditNoteOutlinedIcon sx={{ fontSize: 32, color: "#7C3AED" }} />
          </Box>

          <Typography
            sx={{
              fontSize: { xs: "0.95rem", sm: "1.05rem" },
              fontWeight: 700,
              color: "#111827",
              textTransform: "lowercase",
              mb: 0.5,
            }}
          >
            create manually
          </Typography>
          <Typography
            sx={{
              fontSize: "0.8rem",
              color: "#6b7280",
              textTransform: "lowercase",
              mb: 2,
              lineHeight: 1.5,
            }}
          >
            best for small updates or if you prefer to type everything in
            yourself from scratch.
          </Typography>

          <Button
            variant="outlined"
            fullWidth
            onClick={(e) => {
              e.stopPropagation();
              onSelectManual();
            }}
            sx={{
              borderColor: "#7C3AED",
              color: "#7C3AED",
              bgcolor: "#fff",
              textTransform: "lowercase",
              fontWeight: 600,
              borderRadius: 2,
              py: 1,
              "&:hover": {
                borderColor: "#6200EE",
                color: "#6200EE",
                bgcolor: "#fff",
              },
            }}
          >
            start manual entry
          </Button>
        </Box>
      </Box>

      {/* ── Saved drafts section ───────────────────────────────────────────── */}
      {drafts.length > 0 && (
        <Box sx={{ mt: 3 }}>
          {/* Divider with label */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              mb: 1.5,
            }}
          >
            <Box sx={{ flex: 1, height: "1px", bgcolor: "#e5e7eb" }} />
            <Typography
              sx={{
                fontSize: "0.75rem",
                color: "#9ca3af",
                textTransform: "lowercase",
                whiteSpace: "nowrap",
              }}
            >
              saved drafts
            </Typography>
            <Box sx={{ flex: 1, height: "1px", bgcolor: "#e5e7eb" }} />
          </Box>

          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            {drafts.map((draft) => (
              <Box
                key={draft._id}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                  border: "1px solid #e5e7eb",
                  borderRadius: 2,
                  px: 2,
                  py: 1.25,
                  bgcolor: "#fafafa",
                  transition: "border-color 0.15s",
                  "&:hover": { borderColor: "#7C3AED" },
                }}
              >
                <InsertDriveFileOutlinedIcon
                  sx={{ fontSize: 20, color: "#7C3AED", flexShrink: 0 }}
                />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography
                    sx={{
                      fontSize: "0.85rem",
                      fontWeight: 600,
                      color: "#374151",
                      textTransform: "lowercase",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {draft.name}
                  </Typography>
                  <Typography
                    sx={{ fontSize: "0.72rem", color: "#9ca3af", textTransform: "lowercase" }}
                  >
                    {new Date(
                      draft.updatedAt || draft.createdAt
                    ).toLocaleDateString(undefined, {
                      month: "short",
                      day: "numeric",
                      hour: "2-digit",
                      minute: "2-digit",
                    })}
                  </Typography>
                </Box>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={(e) => {
                    e.stopPropagation();
                    onContinueDraft?.(draft);
                  }}
                  sx={{
                    borderColor: "#7C3AED",
                    color: "#7C3AED",
                    bgcolor: "#fff",
                    textTransform: "lowercase",
                    fontWeight: 600,
                    borderRadius: 1.5,
                    px: 1.5,
                    flexShrink: 0,
                    "&:hover": {
                      borderColor: "#6200EE",
                      color: "#6200EE",
                      bgcolor: "#fff",
                    },
                  }}
                >
                  continue
                </Button>
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    onDeleteDraft?.(draft._id);
                  }}
                  sx={{
                    color: "#DC2626",
                    flexShrink: 0,
                    "&:hover": { bgcolor: "#FEE2E2" },
                  }}
                >
                  <DeleteOutlinedIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </Box>
            ))}
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default MethodSelection;
